import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, ArrowRight, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const { login, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const to = location.state?.from?.pathname || '/admin';

  // Already logged in? bounce to admin
  React.useEffect(() => {
    if (token) navigate(to, { replace: true });
  }, [token, navigate, to]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Please enter both username and password.');
      return;
    }
    setBusy(true);
    try {
      await login(username.trim(), password);
      navigate(to, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Login failed.';
      setError(typeof msg === 'string' ? msg : 'Invalid credentials.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0d0b] text-white relative overflow-hidden grid place-items-center px-4">
      {/* Ambient */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(circle at 50% 30%, #c8942a33, transparent 55%)',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/60 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="bg-white text-[#1c1a17] border border-[#c8942a]/40 shadow-2xl">
          {/* Header */}
          <div className="bg-[#0f0d0b] text-white p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }} />
            <div className="relative">
              <div className="font-sans-pro text-[10px] uppercase tracking-[0.4em] text-[#f0c95a] font-semibold">Whiteplains British School</div>
              <h1 className="mt-2 font-serif-display text-3xl font-black leading-tight">Admissions Console</h1>
              <p className="mt-1 text-white/70 text-sm">Sign in to access enrolments and chats.</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="p-6 space-y-4">
            <label className="block">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8f6417] font-sans-pro mb-1.5">Username</div>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8f6417]" />
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="wbs-input pl-10"
                  placeholder="admin"
                  required
                />
              </div>
            </label>

            <label className="block">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8f6417] font-sans-pro mb-1.5">Password</div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8f6417]" />
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="wbs-input pl-10 pr-10"
                  placeholder="••••••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-[#8f6417] hover:text-[#1c1a17]"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            {error && (
              <div className="text-sm text-[#b8331f] bg-[#fdecea] border border-[#f3c1bb] px-4 py-2.5">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full !justify-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {busy ? (<><Loader2 size={16} className="animate-spin" /> Signing in</>) : (<>Sign in <ArrowRight size={16} /></>)}
            </button>

            <div className="text-[10px] uppercase tracking-[0.28em] text-[#8f6417] text-center pt-2">
              Sessions expire after 12 hours
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
